/**
 * DisposalRecycleBin — lists soft-deleted tools at one site and restores them.
 *
 * ADMIN only on the backend. The page hides the button from non-ADMINs, but
 * that gate is cosmetic: get_deleted_tools and restore_tool enforce the role
 * (and the air gap) themselves, so a 403 is a real path here and is rendered.
 *
 * Loads when it OPENS, not on mount. Rows are the raw snake_case projection
 * from get_deleted_tools, already sorted newest-delete-first by
 * getDeletedDisposalTools.
 *
 * Messages render INLINE in the modal body, never as a toast: spd-toast sits at
 * --z-popover (30) and the ModalShell backdrop at 100, so a toast would paint
 * underneath the dialog that fired it.
 */

import React, { useCallback, useEffect, useState } from 'react';
import ModalShell from '../../shared/components/ModalShell';
import { getDeletedDisposalTools, restoreDisposalTool } from './api/disposalApi';

// Restore refusals that mean this bin's list no longer matches the database:
// the tool is gone, or someone else already restored it.
const STALE_BIN_REASONS = new Set(['not_found', 'not_deleted']);

// The projection minus site_id, in display order.
const BIN_FIELDS = [
  ['tool_id',          'ToolSight ID'],
  ['supplier_tool_id', 'Tool ID'],
  ['die_op',           'Die Op'],
  ['tool_type',        'Tool Type'],
  ['part_description', 'Part Description'],
  ['deleted_by',       'Deleted by'],
  ['deleted_at',       'Deleted at'],
];

// A Cosmos projection OMITS absent keys (undefined), and a key may also hold an
// explicit null. A blank or whitespace-only string is treated the same: the
// Cosmos distinction between an omitted key and a blank string is real, but it
// carries no meaning to an operator recovering a tool, and rendering them
// differently makes a blank field look like a bug. Display only — the deleted_at
// sort in getDeletedDisposalTools keeps its own absence handling.
function isAbsent(v) {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}

// deleted_at is stamp_updated's UTC isoformat(), e.g.
// "2026-09-24T15:04:22.123456+00:00". The shape guard also requires the UTC
// suffix, so " UTC" is only ever appended to a value that is UTC; anything else
// renders RAW rather than being sliced blind.
const UTC_ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(\+00:00|Z)$/;

function displayValue(key, v) {
  if (isAbsent(v)) return '—';
  const s = String(v);
  if (key === 'deleted_at') {
    // Raw strings only per the dates mandate — no date-object parsing, no
    // locale formatting — so the displayed value stays comparable to the sort
    // key: "2026-09-24T15:04:22.123456+00:00" -> "2026-09-24 15:04 UTC".
    return UTC_ISO.test(s) ? `${s.slice(0, 10)} ${s.slice(11, 16)} UTC` : s;
  }
  return s;
}

function errorText(err) {
  // The backend's `error` string is written for the operator: rendered verbatim.
  // No .status means fetch rejected before any Response existed.
  return err.status
    ? err.message
    : "Couldn't reach the server. Check your connection and try again.";
}

export default function DisposalRecycleBin({ isOpen, onClose, siteId, onRestored }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  // { toolLabel, duplicateCount, auditMissing } after a landed restore.
  const [restored, setRestored] = useState(null);
  // { toolLabel, err } after a refused or failed restore.
  const [restoreError, setRestoreError] = useState(null);

  const loadBin = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const body = await getDeletedDisposalTools(siteId);
      setRows(Array.isArray(body.tools) ? body.tools : []);
    } catch (err) {
      setRows([]);
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  // Fresh state and a fresh load on every open.
  useEffect(() => {
    if (!isOpen || !siteId) return;
    setRestored(null);
    setRestoreError(null);
    loadBin();
  }, [isOpen, siteId, loadBin]);

  // One identity (ModalShell's Escape effect lists onClose in its deps). The
  // restoring guard covers every exit, incl. the header X.
  const handleClose = useCallback(() => {
    if (restoringId) return;
    onClose();
  }, [restoringId, onClose]);

  async function handleRestore(row) {
    const toolLabel = isAbsent(row.supplier_tool_id) ? row.tool_id : row.supplier_tool_id;
    setRestoringId(row.tool_id);
    setRestored(null);
    setRestoreError(null);
    try {
      // row.tool_id is the ONLY identity the bin has: the get_deleted_tools
      // projection carries no `id` key. restore_tool point-reads
      // read_item(item=tool_id) and 404s any document whose tool_id differs,
      // so tool_id is exactly the key it needs.
      const result = await restoreDisposalTool(row.tool_id, siteId);
      // audited:false = the Tools write landed, the ChangeLog row did not. The
      // restore still SUCCEEDED. Delete is console-only because its panel
      // unmounts with nowhere for a notice to live (DisposalDetailPanel.jsx:212);
      // the bin's modal stays open and already renders messages, so it uses that
      // surface as well. Deliberate asymmetry, not drift.
      const auditMissing = !!result && result.audited === false;
      if (auditMissing) {
        console.error(`[AUDIT-MISS] restore_tool: tool_id=${row.tool_id} was restored but its ChangeLog audit row is missing`);
      }
      setRows(prev => prev.filter(r => r.tool_id !== row.tool_id));
      // The backend count already excludes the restored tool — used as-is.
      setRestored({ toolLabel, duplicateCount: Number(result?.duplicate_tool_id_count) || 0, auditMissing });
      if (onRestored) onRestored(row.tool_id);
    } catch (err) {
      setRestoreError({ toolLabel, err });
      // The list is out of date: reload it so the refused row reflects reality.
      if (STALE_BIN_REASONS.has(err.body?.reason)) loadBin();
    } finally {
      setRestoringId(null);
    }
  }

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title="Recycle bin"
      footer={
        <button type="button" className="ddp-btn-cancel" onClick={handleClose} disabled={!!restoringId}>
          Close
        </button>
      }
    >
      {restored && (
        <div className="ddp-records-list">
          <div className="drc-bin-ok">Restored {restored.toolLabel}. It is back in the tool list.</div>
          {restored.duplicateCount === 1 && (
            <p className="ddp-record-desc">1 other live tool at this site shares this Tool ID.</p>
          )}
          {restored.duplicateCount > 1 && (
            <p className="ddp-record-desc">{restored.duplicateCount} other live tools at this site share this Tool ID.</p>
          )}
          {/* Additional line, not an error: the restore landed, the audit row did not. */}
          {restored.auditMissing && (
            <p className="ddp-record-desc">Audit log entry for this restore was not recorded.</p>
          )}
        </div>
      )}

      {restoreError && (
        <div className="ddp-records-list">
          <div className="drc-bin-err">
            Couldn't restore {restoreError.toolLabel}: {errorText(restoreError.err)}
          </div>
          {STALE_BIN_REASONS.has(restoreError.err.body?.reason) && (
            <p className="ddp-record-desc">This list was out of date — it has been refreshed.</p>
          )}
        </div>
      )}

      {loading && <div className="drc-loading">Loading deleted tools…</div>}

      {!loading && loadError && (
        <div className="drc-bin-err">{errorText(loadError)}</div>
      )}

      {!loading && !loadError && rows.length === 0 && (
        <div className="dtl-empty">No deleted tools at this site.</div>
      )}

      {!loading && !loadError && rows.length > 0 && (
        <div className="ddp-records-list">
          {rows.map(row => (
            <div className="ddp-record-row" key={row.tool_id}>
              <div className="ddp-grid-2">
                {BIN_FIELDS.map(([key, label]) => (
                  <div className="ddp-field" key={key}>
                    <span className="ddp-label">{label}</span>
                    <span className="ddp-field-value">{displayValue(key, row[key])}</span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="ddp-btn-save"
                onClick={() => handleRestore(row)}
                disabled={!!restoringId}
              >
                {restoringId === row.tool_id ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          ))}
        </div>
      )}
    </ModalShell>
  );
}
